import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeQuery,
  useUser,
} from "@tellescope/tanstack-query";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { useMemo, useState } from "react";
import { buildTemplateAudienceMdbFilter } from "./templateAudienceFilter";

const TEMPLATE_SEARCH_MIN_SCORE = 0.4;

export interface InboxTemplateSelectorContextFilters {
  channels?: TellescopeModelFromName<"templates">["forChannels"];
  enduserOnly?: boolean;
  userId?: string;
  enduserId?: string;
}

export const useInboxTemplateSelector = ({
  contextFilters,
  enableQuery,
}: {
  contextFilters?: InboxTemplateSelectorContextFilters;
  enableQuery: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);
  const [previewedTemplateId, setPreviewedTemplateId] = useState<string | undefined>(undefined);

  const user = useUser(contextFilters?.userId ?? "", !!contextFilters?.userId);
  const enduser = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: contextFilters?.enduserId ?? "",
    enabled: !!contextFilters?.enduserId,
  });

  const templatesQuery = useTellescopeInfiniteQuery({
    model: "templates",
    method: "getSome",
    request: {
      search: debouncedSearchQuery
        ? { query: debouncedSearchQuery, minSearchScore: TEMPLATE_SEARCH_MIN_SCORE }
        : undefined,
      mdbFilter: buildTemplateAudienceMdbFilter({
        types: contextFilters?.enduserOnly ? ["enduser"] : ["enduser", "team"],
        channels: contextFilters?.channels,
        userRoles: user?.roles,
        userTags: user?.tags,
        enduserCustomTypeId: enduser.data?.customTypeId,
      }),
    },
    enabled: enableQuery,
    select: (data) => data.pages.flat(),
  });

  const templates = templatesQuery.data;

  const previewedTemplate = useMemo(
    () => templates?.find((template) => template.id === previewedTemplateId) ?? templates?.[0],
    [templates, previewedTemplateId]
  );

  return {
    searchQuery,
    setSearchQuery,
    isSearchPending: searchQuery !== debouncedSearchQuery,
    templates,
    isLoading: templatesQuery.isLoading,
    fetchNextPage: templatesQuery.fetchNextPage,
    previewedTemplate,
    setPreviewedTemplateId,
  };
};
